import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  fetchGroupMembers,
  addGroupMember,
  removeGroupMember,
  fetchDeskStatuses,
  fetchDeskRequestTypes,
  fetchGateTransitions,
  fetchGpsUsers,
  type ValidationGroup,
  type GroupMember,
  type DeskStatus,
  type DeskRequestType,
  type GateTransition,
  type GpsUser,
  type CreateGroupInput,
  type UpdateGroupInput,
} from '../api/validationGroupsApi';

const GROUPS_KEY = 'validationGroups';

export const useValidationGroups = (serviceDeskId?: number) =>
  useQuery<ValidationGroup[]>({
    queryKey: [GROUPS_KEY, serviceDeskId ?? 'all'],
    queryFn: () => fetchGroups(serviceDeskId),
  });

export const useCreateGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGroupInput) => createGroup(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: [GROUPS_KEY] }),
  });
};

export const useUpdateGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateGroupInput }) => updateGroup(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [GROUPS_KEY] }),
  });
};

export const useDeleteGroup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteGroup(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [GROUPS_KEY] }),
  });
};

export const useGroupMembers = (groupId: number | null) =>
  useQuery<GroupMember[]>({
    queryKey: [GROUPS_KEY, 'members', groupId],
    queryFn: () => fetchGroupMembers(groupId as number),
    enabled: groupId != null,
  });

export const useAddGroupMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: number; userId: number }) => addGroupMember(groupId, userId),
    onSuccess: (_d, { groupId }) => {
      qc.invalidateQueries({ queryKey: [GROUPS_KEY, 'members', groupId] });
      qc.invalidateQueries({ queryKey: [GROUPS_KEY] });
    },
  });
};

export const useRemoveGroupMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: number; userId: number }) => removeGroupMember(groupId, userId),
    onSuccess: (_d, { groupId }) => {
      qc.invalidateQueries({ queryKey: [GROUPS_KEY, 'members', groupId] });
      qc.invalidateQueries({ queryKey: [GROUPS_KEY] });
    },
  });
};

export const useDeskStatuses = (serviceDeskId: number | null) =>
  useQuery<DeskStatus[]>({
    queryKey: [GROUPS_KEY, 'statuses', serviceDeskId],
    queryFn: () => fetchDeskStatuses(serviceDeskId as number),
    enabled: serviceDeskId != null,
    staleTime: 5 * 60 * 1000,
  });

export const useDeskRequestTypes = (serviceDeskId: number | null) =>
  useQuery<DeskRequestType[]>({
    queryKey: [GROUPS_KEY, 'requestTypes', serviceDeskId],
    queryFn: () => fetchDeskRequestTypes(serviceDeskId as number),
    enabled: serviceDeskId != null,
    staleTime: 5 * 60 * 1000,
  });

export const useGateTransitions = (serviceDeskId: number | null, statusId: number | null) =>
  useQuery<GateTransition[]>({
    queryKey: [GROUPS_KEY, 'transitions', serviceDeskId, statusId],
    queryFn: () => fetchGateTransitions(serviceDeskId as number, statusId as number),
    enabled: serviceDeskId != null && statusId != null,
    staleTime: 5 * 60 * 1000,
  });

export const useGpsUsers = () =>
  useQuery<GpsUser[]>({
    queryKey: ['gpsUsers'],
    queryFn: fetchGpsUsers,
    staleTime: 5 * 60 * 1000,
  });
